/**
 * WB4 Wave 2 — Session-scoped finance reversal pending state (requires_approval FSM).
 */

import type { WorkbenchTimelineObjectRef } from './workbench-timeline-event';
import { buildWorkbenchTimelineObjectRefKey } from './workbench-timeline-event';

export const WORKBENCH_FINANCE_REVERSAL_PENDING_STATE =
  'workbench_finance_reversal_pending' as const;

export interface WorkbenchFinanceReversalPendingEntry {
  state: typeof WORKBENCH_FINANCE_REVERSAL_PENDING_STATE;
  tenantId: string;
  objectRef: WorkbenchTimelineObjectRef;
  requestId: string;
  sourceVoucherId: string;
  actionLabel: string;
  requestedAt: string;
  reversalRequestStatus: 'pending' | 'posted' | 'cancelled';
}

const pendingByObjectKey = new Map<string, WorkbenchFinanceReversalPendingEntry>();

function buildPendingKey(tenantId: string, objectRef: WorkbenchTimelineObjectRef): string {
  return `${tenantId}:${buildWorkbenchTimelineObjectRefKey(objectRef)}`;
}

export function markWorkbenchFinanceReversalPending(input: {
  tenantId: string;
  objectRef: WorkbenchTimelineObjectRef;
  requestId: string;
  sourceVoucherId: string;
  actionLabel: string;
  requestedAt?: string;
  reversalRequestStatus?: 'pending' | 'posted' | 'cancelled';
}): WorkbenchFinanceReversalPendingEntry {
  const entry: WorkbenchFinanceReversalPendingEntry = {
    state: WORKBENCH_FINANCE_REVERSAL_PENDING_STATE,
    tenantId: input.tenantId,
    objectRef: input.objectRef,
    requestId: input.requestId,
    sourceVoucherId: input.sourceVoucherId,
    actionLabel: input.actionLabel,
    requestedAt: input.requestedAt ?? new Date().toISOString(),
    reversalRequestStatus: input.reversalRequestStatus ?? 'pending',
  };
  pendingByObjectKey.set(buildPendingKey(input.tenantId, input.objectRef), entry);
  return entry;
}

export function getWorkbenchFinanceReversalPending(
  tenantId: string,
  objectRef: WorkbenchTimelineObjectRef
): WorkbenchFinanceReversalPendingEntry | null {
  return pendingByObjectKey.get(buildPendingKey(tenantId, objectRef)) ?? null;
}

export function clearWorkbenchFinanceReversalPending(
  tenantId: string,
  objectRef: WorkbenchTimelineObjectRef,
  requestId?: string
): boolean {
  const key = buildPendingKey(tenantId, objectRef);
  const existing = pendingByObjectKey.get(key);
  if (!existing) {
    return false;
  }
  if (requestId && existing.requestId !== requestId) {
    return false;
  }
  pendingByObjectKey.delete(key);
  return true;
}

/** @internal test-only */
export function resetWorkbenchFinanceReversalPendingStateForTests(): void {
  pendingByObjectKey.clear();
}
